/**
 * Game mode logic: presets, layered mode state, game-over and win rules.
 * Pure functions — no side effects, no rendering.
 *
 * A match is one base rule (elimination + optional turn limit) with layers
 * stacked on it (see core/modeRules.ts):
 *   - one objective layer: stakes, ctf, signalTrace, uplink, salvage, excavation, or none
 *   - any environment layers: blackout, lastLight
 *   - an optional respawn layer (ticket pools; a team is out only when permadead)
 *
 * Win precedence, in order:
 *   1. an objective completed outright (captures, extraction, upload, artifact home)
 *   2. the field is down to one team (or none): objectives that score
 *      standing progress (territory, salvage) rank by it first; otherwise
 *      the last team standing wins by elimination
 *   3. the turn limit was reached, or every environment schedule ran out:
 *      the objective ranks the teams (deathmatch ranks survivors > kills > damage)
 */

import type {
  GameState, GameMode, ModeConfig, ModeState, WinResult, PlanetState, PlayerState, ObjectiveKey, EnvironmentKey, ModeRules,
} from '../types';
import { computeTerritoryScores } from './stakes';
import { timeLimitReached } from './clockRules';
import { isTeamPermadead } from './respawn';
import { pointsHeld } from './capturePoints';
import { winConditionsMet } from './winConditions';
import { objectivesDone, objectivesDoneCount } from './storyObjectives';
import { initAlertState } from './perception';
import { getModeRules, objectiveUsesInteractions } from './modeRules';
import {
  CTF_CAPTURES_REQUIRED, DEFAULT_RESPAWN_TICKETS, DEFAULT_RESPAWN_COOLDOWN,
  DEFAULT_POSTS_TO_TRIANGULATE, DEFAULT_POST_ACTIVATION_TURNS,
  DEFAULT_EXTRACTION_TURNS, DEFAULT_FRAGMENTS_REQUIRED,
  DEFAULT_VAULT_ACTIVATION_TURNS, DEFAULT_UPLOAD_TURNS,
  DEFAULT_SALVAGE_NODE_COUNT, DEFAULT_DRILL_BUILD_TIME,
  DEFAULT_DRILL_OPERATION_TIME, DEFAULT_DRILL_SITE_HP,
  DEFAULT_BLACKOUT_START_TURN, DEFAULT_DESTRUCTION_INTERVAL,
  DEFAULT_FINAL_PLANETS_COUNT, DEFAULT_DARK_VISIBILITY,
} from '../constants';
import { initClueState } from './clues';
import { initBlackoutState } from './blackout';
import { initLastLightState } from './lastLight';

export { getModeRules } from './modeRules';

// =============================================================================
// MODE DEFAULTS (preset config bundles)
// =============================================================================

export function getDefaultModeConfig(mode: GameMode): ModeConfig {
  switch (mode) {
    case 'dominion':
      return { mode, stakesPerUnit: 2, turnLimit: 60, multiStake: true, stakeVisible: true };
    case 'sovereignty':
      return { mode, stakesPerUnit: 3, stakeHP: 5, stakeVisible: false, scanRange: 3.0, multiStake: false, turnLimit: 60 };
    case 'ctf':
      return {
        mode,
        capturesRequired: CTF_CAPTURES_REQUIRED,
        respawnTickets: DEFAULT_RESPAWN_TICKETS,
        respawnCooldown: DEFAULT_RESPAWN_COOLDOWN,
        carrierSpeedPenalty: 0.5,
        carrierCanJump: true,
        turnLimit: 40,
      };
    case 'signalTrace':
      return {
        mode,
        postsToTriangulate: DEFAULT_POSTS_TO_TRIANGULATE,
        postActivationTurns: DEFAULT_POST_ACTIVATION_TURNS,
        extractionTurns: DEFAULT_EXTRACTION_TURNS,
        respawnTickets: 10,
        respawnCooldown: DEFAULT_RESPAWN_COOLDOWN,
        turnLimit: 60,
      };
    case 'uplink':
      return {
        mode,
        fragmentsRequired: DEFAULT_FRAGMENTS_REQUIRED,
        vaultActivationTurns: DEFAULT_VAULT_ACTIVATION_TURNS,
        uploadTurns: DEFAULT_UPLOAD_TURNS,
        respawnTickets: 10,
        respawnCooldown: DEFAULT_RESPAWN_COOLDOWN,
        carrierSpeedPenalty: 0.3,
        turnLimit: 40,
      };
    case 'salvageRun':
      return {
        mode,
        salvageNodeCount: DEFAULT_SALVAGE_NODE_COUNT,
        unitCarryCapacity: 3,
        carrierSpeedPenalty: 0.3,
        turnLimit: 40,
      };
    case 'excavation':
      return {
        mode,
        drillBuildTime: DEFAULT_DRILL_BUILD_TIME,
        drillOperationTime: DEFAULT_DRILL_OPERATION_TIME,
        drillSiteHP: DEFAULT_DRILL_SITE_HP,
        respawnTickets: 10,
        respawnCooldown: DEFAULT_RESPAWN_COOLDOWN,
        turnLimit: 40,
      };
    case 'blackout':
      return {
        mode,
        blackoutRate: 1,
        blackoutDelay: 1,
        blackoutStartTurn: DEFAULT_BLACKOUT_START_TURN,
        blackoutOrder: 'random',
        visibilityOnDark: DEFAULT_DARK_VISIBILITY,
        turnLimit: 80,
      };
    case 'lastLight':
      return {
        mode,
        destructionInterval: DEFAULT_DESTRUCTION_INTERVAL,
        destructionOrder: 'innerFirst',
        warningTurns: 2,
        finalPlanetsCount: DEFAULT_FINAL_PLANETS_COUNT,
        turnLimit: 60,
      };
    default:
      return { mode };
  }
}

/**
 * Default config bundle for an environment layer, for stacking one onto a
 * preset that does not carry its settings (e.g. Dominion + Blackout).
 */
export function getEnvironmentDefaults(env: EnvironmentKey): Partial<ModeConfig> {
  const bundle = getDefaultModeConfig(env);
  const { mode: _mode, turnLimit: _turnLimit, ...rest } = bundle;
  return rest;
}

/**
 * Compose a match config from a preset plus modifiers: the preset's bundle,
 * the defaults of every environment layer stacked on top, an explicit
 * `environments` list, and the turn limit (the preset's when not given).
 */
export function composeModeConfig(
  mode: GameMode,
  options: { turnLimit?: number; modifiers?: EnvironmentKey[] } = {},
): ModeConfig {
  const preset = getDefaultModeConfig(mode);
  const presetEnvs = getModeRules(preset).environments;
  const extras = (options.modifiers ?? []).filter(e => !presetEnvs.includes(e));
  let config: ModeConfig = { ...preset };
  for (const env of extras) config = { ...getEnvironmentDefaults(env), ...config };
  if (extras.length > 0) config.environments = [...presetEnvs, ...extras];
  config.turnLimit = options.turnLimit ?? preset.turnLimit;
  return config;
}

// =============================================================================
// MODE STATE
// =============================================================================

/**
 * Build the composite state for a config: one block per active layer.
 * Objective blocks start empty; game-start setup (core/modeSetup.ts) fills in
 * the placements (home planets, signal source, depots, ...).
 */
export function initModeState(config: ModeConfig, planets?: PlanetState[], seed?: string): ModeState {
  const rules = getModeRules(config);
  const ms: ModeState = {};
  // Phase 57: the alert layer, when the match asks for one (absent otherwise — today's game)
  const alert = planets ? initAlertState(config, planets) : undefined;
  if (alert) ms.alert = alert;

  switch (rules.objective) {
    case 'stakes':
      ms.territory = { territoryScores: {}, detectedFlags: {} };
      break;
    case 'ctf':
      ms.ctf = { scores: {}, flagStatus: {} };
      break;
    case 'signalTrace':
      ms.signalTrace = {
        signalPlanetIndex: -1,
        signalSurfaceDir: { x: 0, y: 1, z: 0 },
        clueState: initClueState(),
        extraction: null,
        extractionCompletedTeam: null,
      };
      break;
    case 'uplink':
      ms.uplink = { antennaRuinId: -1, uploadedFragments: {} };
      break;
    case 'salvage':
      ms.salvage = { depotPlanets: {}, deliveredValue: {} };
      break;
    case 'excavation':
      ms.excavation = {
        artifactPlanetIndex: -1,
        artifactSurfaceDir: { x: 0, y: 1, z: 0 },
        drillSites: [],
        artifactExtracted: false,
        artifactCarryableId: null,
      };
      break;
    case 'points':
      ms.points = { sites: [], bleedClock: 0 };   // placed after deploy (finalizeModeDeploy)
      break;
    case 'none':
      break;
  }

  if (objectiveUsesInteractions(rules.objective)) ms.interactionProgress = [];
  if (rules.respawn) ms.respawn = { tickets: {}, cooldowns: {} };

  for (const env of rules.environments) {
    if (env === 'blackout') ms.blackout = initBlackoutState(config, planets ?? [], seed ?? 'default');
    if (env === 'lastLight') ms.lastLight = initLastLightState(config, planets ?? [], seed ?? 'default');
  }

  return ms;
}

// =============================================================================
// TEAMS
// =============================================================================

function allTeams(state: GameState): number[] {
  return [...new Set(state.players.map(p => p.teamIndex))];
}

/**
 * Teams still in the fight. Without respawn that is every team with a living
 * unit; with respawn a team is out only once it is permadead (no units, no
 * tickets).
 */
export function teamsInPlay(state: GameState, rules: ModeRules = getModeRules(state.config.modeConfig)): number[] {
  const respawn = rules.respawn ? state.modeState.respawn : undefined;
  if (respawn) {
    return allTeams(state).filter(ti => !isTeamPermadead(ti, respawn, state.players));
  }
  return [...new Set(state.players.filter(p => p.alive).map(p => p.teamIndex))];
}

function representative(state: GameState, teamIndex: number): PlayerState {
  return state.players.find(p => p.teamIndex === teamIndex && p.alive)
    ?? state.players.find(p => p.teamIndex === teamIndex)!;
}

function eliminationWin(state: GameState, teamIndex: number): WinResult {
  return { winningTeamIndex: teamIndex, winningPlayer: representative(state, teamIndex), reason: 'elimination' };
}

/** 64.4: the turn limit, or on the action clock the time limit in seconds — one rule, read through core/clockRules.ts. */
function turnLimitReached(state: GameState): boolean {
  return timeLimitReached(state);
}

/** Every environment layer has run its schedule to the end (the map stopped changing). */
export function environmentsExhausted(state: GameState): boolean {
  const ms = state.modeState;
  const schedules: number[][] = [];
  if (ms.blackout) schedules.push(ms.blackout.schedule);
  if (ms.lastLight) schedules.push(ms.lastLight.schedule);
  return schedules.length > 0 && schedules.every(s => s.length === 0);
}

// =============================================================================
// RANKING — generic "sort teams, draw on a full tie" helper
// =============================================================================

type Ranked = Array<[number, number[]]>;

/**
 * Sort teams by their stat vectors (descending, lexicographic). Returns null
 * when the top two teams tie on every stat (a draw).
 */
function rankTeams(entries: Ranked): Ranked | null {
  const sorted = [...entries].sort((a, b) => {
    for (let i = 0; i < a[1].length; i++) {
      if (b[1][i] !== a[1][i]) return b[1][i] - a[1][i];
    }
    return 0;
  });
  if (sorted.length >= 2) {
    const [first, second] = sorted;
    if (first[1].every((v, i) => v === second[1][i])) return null;
  }
  return sorted;
}

function teamAlive(state: GameState, teamIndex: number): number {
  return state.players.filter(p => p.teamIndex === teamIndex && p.alive).length;
}

function teamKills(state: GameState, teamIndex: number): number {
  return state.players.filter(p => p.teamIndex === teamIndex).reduce((s, p) => s + p.kills, 0);
}

function teamDamage(state: GameState, teamIndex: number): number {
  return state.players.filter(p => p.teamIndex === teamIndex).reduce((s, p) => s + p.damageDealt, 0);
}

// =============================================================================
// OBJECTIVE RULES
// =============================================================================

interface ObjectiveRule {
  /** The objective was completed outright this instant. */
  checkObjective(state: GameState): WinResult | null;
  /**
   * Rank the teams on objective progress when the match ends on time or on the
   * field emptying. Null = draw (or nothing to rank on).
   */
  rank(state: GameState, teams: number[], reason: WinResult['reason']): WinResult | null;
  /**
   * When the field is down to one team, standing progress decides before
   * elimination does (territory held, salvage banked).
   */
  decidesOnElimination: boolean;
}

/** Deathmatch ranking: survivors > kills > damage. */
const noObjective: ObjectiveRule = {
  checkObjective: () => null,
  decidesOnElimination: false,
  rank(state, teams, reason) {
    const ranked = rankTeams(teams.map(ti => [ti, [teamAlive(state, ti), teamKills(state, ti), teamDamage(state, ti)]]));
    if (!ranked) return null;
    const teamScores: Record<number, number> = {};
    for (const [ti, [alive, kills, damage]] of ranked) teamScores[ti] = alive * 1000 + kills * 10 + Math.round(damage);
    return { winningTeamIndex: ranked[0][0], winningPlayer: representative(state, ranked[0][0]), reason, teamScores };
  },
};

/** Territory: most owned planets, then most alive stakes. */
const stakesObjective: ObjectiveRule = {
  checkObjective: () => null,
  decidesOnElimination: true,
  rank(state, teams, reason) {
    const territoryScores = computeTerritoryScores(state);
    const aliveStakes = new Map<number, number>();
    for (const f of state.plantedFlags) {
      if (f.alive !== false) aliveStakes.set(f.teamIndex, (aliveStakes.get(f.teamIndex) ?? 0) + 1);
    }
    const ranked = rankTeams(teams.map(ti => [ti, [territoryScores.get(ti) ?? 0, aliveStakes.get(ti) ?? 0]]));
    if (!ranked) return null;
    const teamScores: Record<number, number> = {};
    for (const [ti, [planets, stakes]] of ranked) teamScores[ti] = planets * 1000 + stakes;
    return { winningTeamIndex: ranked[0][0], winningPlayer: representative(state, ranked[0][0]), reason, teamScores };
  },
};

/** CTF: reach the capture count outright; on time, most captures then most alive. */
const ctfObjective: ObjectiveRule = {
  decidesOnElimination: false,
  checkObjective(state) {
    const ctf = state.modeState.ctf;
    if (!ctf) return null;
    const required = state.config.modeConfig.capturesRequired ?? CTF_CAPTURES_REQUIRED;
    const teams = allTeams(state);
    for (const ti of teams) {
      if ((ctf.scores[ti] ?? 0) >= required) {
        const teamScores: Record<number, number> = {};
        for (const t of teams) teamScores[t] = ctf.scores[t] ?? 0;
        return { winningTeamIndex: ti, winningPlayer: representative(state, ti), reason: 'objective', teamScores };
      }
    }
    return null;
  },
  rank(state, teams, reason) {
    const ctf = state.modeState.ctf;
    if (!ctf) return null;
    const ranked = rankTeams(teams.map(ti => [ti, [ctf.scores[ti] ?? 0, teamAlive(state, ti)]]));
    if (!ranked) return null;
    const teamScores: Record<number, number> = {};
    for (const [ti, [captures, alive]] of ranked) teamScores[ti] = captures * 1000 + alive;
    return { winningTeamIndex: ranked[0][0], winningPlayer: representative(state, ranked[0][0]), reason, teamScores };
  },
};

/** Survivors then kills, no score table (signal trace, excavation on time). */
function rankByElimination(state: GameState, teams: number[], reason: WinResult['reason']): WinResult | null {
  const ranked = rankTeams(teams.map(ti => [ti, [teamAlive(state, ti), teamKills(state, ti)]]));
  if (!ranked) return null;
  return { winningTeamIndex: ranked[0][0], winningPlayer: representative(state, ranked[0][0]), reason };
}

/** Signal Trace: a completed extraction wins outright. */
const signalTraceObjective: ObjectiveRule = {
  decidesOnElimination: false,
  checkObjective(state) {
    const st = state.modeState.signalTrace;
    if (!st || st.extractionCompletedTeam == null) return null;
    const ti = st.extractionCompletedTeam;
    return { winningTeamIndex: ti, winningPlayer: representative(state, ti), reason: 'objective' };
  },
  rank: rankByElimination,
};

/** Uplink: fragments from enough distinct planets win outright; on time, most fragments then most alive. */
const uplinkObjective: ObjectiveRule = {
  decidesOnElimination: false,
  checkObjective(state) {
    const up = state.modeState.uplink;
    if (!up) return null;
    const required = state.config.modeConfig.fragmentsRequired ?? DEFAULT_FRAGMENTS_REQUIRED;
    const teams = allTeams(state);
    for (const ti of teams) {
      const fragments = up.uploadedFragments[ti] ?? [];
      if (new Set(fragments).size >= required) {
        const teamScores: Record<number, number> = {};
        for (const t of teams) teamScores[t] = (up.uploadedFragments[t] ?? []).length;
        return { winningTeamIndex: ti, winningPlayer: representative(state, ti), reason: 'objective', teamScores };
      }
    }
    return null;
  },
  rank(state, teams, reason) {
    const up = state.modeState.uplink;
    if (!up) return null;
    const ranked = rankTeams(teams.map(ti => [ti, [(up.uploadedFragments[ti] ?? []).length, teamAlive(state, ti)]]));
    if (!ranked) return null;
    const teamScores: Record<number, number> = {};
    for (const [ti, [fragments, alive]] of ranked) teamScores[ti] = fragments * 1000 + alive;
    return { winningTeamIndex: ranked[0][0], winningPlayer: representative(state, ranked[0][0]), reason, teamScores };
  },
};

/** Salvage: most delivered value, then most alive. Nothing delivered cannot decide an emptied field. */
const salvageObjective: ObjectiveRule = {
  checkObjective: () => null,
  decidesOnElimination: true,
  rank(state, teams, reason) {
    const sv = state.modeState.salvage;
    if (!sv) return null;
    const ranked = rankTeams(teams.map(ti => [ti, [sv.deliveredValue[ti] ?? 0, teamAlive(state, ti)]]));
    if (!ranked) return null;
    if (reason === 'objective' && ranked[0][1][0] === 0) return null;
    const teamScores: Record<number, number> = {};
    for (const [ti, [value]] of ranked) teamScores[ti] = value;
    return { winningTeamIndex: ranked[0][0], winningPlayer: representative(state, ranked[0][0]), reason, teamScores };
  },
};

/**
 * Excavation objective: the extracted artifact is carried by a living player
 * standing on their team's home planet. Without home planets (older configs)
 * any living carrier wins.
 */
export function findExcavationWinner(state: GameState): PlayerState | null {
  const ex = state.modeState.excavation;
  if (!ex || !ex.artifactExtracted || ex.artifactCarryableId == null) return null;
  const artifact = state.carryables.find(c => c.id === ex.artifactCarryableId);
  if (!artifact || artifact.carriedBy == null) return null;
  const carrier = state.players.find(p => p.id === artifact.carriedBy);
  if (!carrier || !carrier.alive || carrier.airborne) return null;
  const home = state.config.modeConfig.homeTeamPlanets?.[carrier.teamIndex];
  if (home !== undefined && carrier.planetIndex !== home) return null;
  return carrier;
}

const excavationObjective: ObjectiveRule = {
  decidesOnElimination: false,
  checkObjective(state) {
    const carrier = findExcavationWinner(state);
    return carrier ? { winningTeamIndex: carrier.teamIndex, winningPlayer: carrier, reason: 'objective' } : null;
  },
  rank: rankByElimination,
};

/**
 * 59.6 Capture points (conquest): tickets are the score — bled by holding fewer points, spent on
 * respawns. The last team with tickets left wins outright; on time, most points held, then most
 * tickets, then most alive.
 */
const pointsObjective: ObjectiveRule = {
  decidesOnElimination: false,
  checkObjective(state) {
    const respawn = state.modeState.respawn;
    if (!state.modeState.points || !respawn) return null;
    const pts = state.modeState.points;
    // 69.6d: an Operations push is won by taking the last sector; out of tickets, the attackers lose (below)
    if (pts.push && pts.front && (pts.push.active >= pts.front.count || pts.push.active < 0)) {
      // 69.6e: in the tug of war the front past either base sector wins it for the side that pushed it there
      const winner = pts.push.active < 0 ? pts.push.defender : pts.push.attacker;
      const scores: Record<number, number> = {};
      for (const t of allTeams(state)) scores[t] = respawn.tickets[t] ?? 0;
      return { winningTeamIndex: winner, winningPlayer: representative(state, winner), reason: 'objective', teamScores: scores };
    }
    const teams = allTeams(state);
    // §0.11 step A (ModeConfig.winConditions): the first side to meet `needed` of the battle's ways to win; the most met
    // first if two do at once, then the usual rank. Instead of the last-with-tickets rule (tickets are one of the ways)
    const wc = state.config.modeConfig.winConditions;
    if (wc) {
      const winners = teams.filter(t => winConditionsMet(state, t) >= wc.needed);
      if (winners.length === 0 || teams.length < 2) return null;
      // 69.96 (DECISIONS 317): in the Story a side out of tickets still ends it, but the sides rank as at time (story
      // objectives done first) — unless a side has done all its objectives, which wins outright as before
      if (getModeRules(state.config.modeConfig).timeVerdict === 'storyObjectives' && wc.use.includes('tickets')
        && teams.some(t => (respawn.tickets[t] ?? 0) <= 0) && !winners.some(t => objectivesDone(state, t))) {
        const ranked = pointsObjective.rank(state, teams, 'turnLimit');
        return ranked ? { ...ranked, reason: 'objective' } : null;
      }
      return pointsObjective.rank(state, winners, 'objective');
    }
    const withTickets = teams.filter(t => (respawn.tickets[t] ?? 0) > 0);
    if (teams.length < 2 || withTickets.length !== 1) return null;
    const ti = withTickets[0];
    const teamScores: Record<number, number> = {};
    for (const t of teams) teamScores[t] = respawn.tickets[t] ?? 0;
    return { winningTeamIndex: ti, winningPlayer: representative(state, ti), reason: 'objective', teamScores };
  },
  rank(state, teams, reason) {
    // 69.6d: a push that runs out of time is the defenders' — they held
    const push = state.modeState.points?.front ? state.modeState.points.push : undefined;
    // 69.6e: a tug of war on time goes to the side that has pushed the front past the middle; at the middle, the usual rank
    if (push?.shape === 'tug') {
      // the side holding more whole sectors
      const pts = state.modeState.points!;
      const held = (t: number) => Array.from({ length: pts.front!.count }, (_, k) => pts.sites.filter(s => s.sector === k)).filter(own => own.length > 0 && own.every(s => s.owner === t && s.progress === 0)).length;
      const a = held(push.attacker), d = held(push.defender);
      const ahead = a > d ? push.attacker : d > a ? push.defender : null;
      if (ahead !== null && teams.includes(ahead)) {
        const scores: Record<number, number> = {};
        for (const t of teams) scores[t] = t === ahead ? 1 : 0;
        return { winningTeamIndex: ahead, winningPlayer: representative(state, ahead), reason, teamScores: scores };
      }
    } else if (push && teams.includes(push.defender)) {
      const scores: Record<number, number> = {};
      for (const t of teams) scores[t] = t === push.defender ? 1 : 0;
      return { winningTeamIndex: push.defender, winningPlayer: representative(state, push.defender), reason, teamScores: scores };
    }
    const held = pointsHeld(state.modeState.points);
    const tickets = state.modeState.respawn?.tickets ?? {};
    // (§0.11 step A: with win conditions set, the side meeting the most of them first; the most held only with 'time' on)
    const wc = state.config.modeConfig.winConditions;
    // (69.96, DECISIONS 316: the Story at time — its story objectives done first, a tie falling to the order here, in place
    // of the conditions met: none is met at time, and 317's tickets out, ranked the same way, is one met)
    const byStory = reason === 'turnLimit' && getModeRules(state.config.modeConfig).timeVerdict === 'storyObjectives';
    const keys = (ti: number) => !wc ? [held.get(ti) ?? 0, tickets[ti] ?? 0, teamAlive(state, ti)]
      : [byStory ? objectivesDoneCount(state, ti) : winConditionsMet(state, ti), ...(wc.use.includes('time') || reason === 'objective' ? [held.get(ti) ?? 0, tickets[ti] ?? 0, teamAlive(state, ti)] : [])];
    const ranked = rankTeams(teams.map(ti => [ti, keys(ti)]));
    if (!ranked) return null;
    const teamScores: Record<number, number> = {};
    for (const [ti] of ranked) teamScores[ti] = (held.get(ti) ?? 0) * 1000 + (tickets[ti] ?? 0);
    return { winningTeamIndex: ranked[0][0], winningPlayer: representative(state, ranked[0][0]), reason, teamScores };
  },
};

const OBJECTIVES: Record<ObjectiveKey, ObjectiveRule> = {
  points: pointsObjective,
  none: noObjective,
  stakes: stakesObjective,
  ctf: ctfObjective,
  signalTrace: signalTraceObjective,
  uplink: uplinkObjective,
  salvage: salvageObjective,
  excavation: excavationObjective,
};

// =============================================================================
// GAME OVER + WIN
// =============================================================================

/**
 * Is the match over? Same precedence as checkModeWinCondition, but a draw
 * (field empty, tied ranking) still counts as over.
 */
export function isModeGameOver(state: GameState): boolean {
  const rules = getModeRules(state.config.modeConfig);
  if (OBJECTIVES[rules.objective].checkObjective(state)) return true;
  if (teamsInPlay(state, rules).length <= 1) return true;
  if (turnLimitReached(state)) return true;
  if (environmentsExhausted(state)) return true;
  return false;
}

/**
 * 57.7c: one team's objective progress as a number that only grows with work done — the objective's own
 * ranking score (captures, delivered value, fragments, stakes, ...) without its alive tie-break, plus a point
 * per enemy carryable the team is carrying, so picking the thing up counts before delivering it does.
 * Deathmatch: kills.
 */
export function objectiveProgress(state: GameState, teamIndex: number): number {
  const rules = getModeRules(state.config.modeConfig);
  const objective = OBJECTIVES[rules.objective];
  let v = 0;
  if (rules.objective === 'none') v = teamKills(state, teamIndex);
  else if (rules.objective === 'signalTrace') v = (state.modeState.signalTrace?.clueState.activatedPosts[teamIndex] ?? []).length * 1000;
  else {
    const r = objective.rank(state, allTeams(state), 'turnLimit');
    v = r?.teamScores?.[teamIndex] ?? 0;
    if (rules.objective === 'ctf' || rules.objective === 'uplink') v = Math.floor(v / 1000) * 1000;   // drop the alive tie-break
  }
  for (const c of state.carryables) {
    const carrier = c.carriedBy === null ? undefined : state.players.find(p => p.id === c.carriedBy);
    if (carrier && carrier.teamIndex === teamIndex && c.teamIndex !== teamIndex) v += 1;
  }
  return v;
}

/** 57.7c: does this team lead the objective's ranking right now (a draw is not a lead)? */
export function teamLeads(state: GameState, teamIndex: number): boolean {
  const rules = getModeRules(state.config.modeConfig);
  const r = OBJECTIVES[rules.objective].rank(state, allTeams(state), 'turnLimit');
  return r?.winningTeamIndex === teamIndex;
}

/**
 * Determine the winner. Returns null while the match is in progress and on a
 * draw.
 */
export function checkModeWinCondition(state: GameState): WinResult | null {
  const rules = getModeRules(state.config.modeConfig);
  const objective = OBJECTIVES[rules.objective];
  const teams = allTeams(state);

  const outright = objective.checkObjective(state);
  if (outright) return outright;

  const inPlay = teamsInPlay(state, rules);
  if (inPlay.length <= 1) {
    if (objective.decidesOnElimination) {
      const byProgress = objective.rank(state, teams, 'objective');
      if (byProgress) return byProgress;
    }
    if (inPlay.length === 1) return eliminationWin(state, inPlay[0]);
    return null;
  }

  if (turnLimitReached(state) || environmentsExhausted(state)) {
    return objective.rank(state, teams, 'turnLimit');
  }

  return null;
}
